
import { useCallback, useRef, useEffect } from "react";
import { GraphData, BoundingBox } from "../useGraphData";

interface UseGraphInitializationProps {
  graphRef: React.MutableRefObject<any>;
  isGraphReady: boolean;
  centerView: (graphData: GraphData) => BoundingBox | null;
  applyInitialZoom: () => void;
}

export const useGraphInitialization = ({
  graphRef,
  isGraphReady,
  centerView,
  applyInitialZoom
}: UseGraphInitializationProps) => {
  const pendingDataChange = useRef(false);
  const initialCenteringComplete = useRef(false);

  const initializeGraphRender = useCallback((graphData: GraphData) => {
    if (isGraphReady && graphRef.current && pendingDataChange.current) {
      console.log("Data changed, applying centering and zooming");
      
      const currentOperation = { current: "init" };
      
      setTimeout(() => {
        const newBoundingBox = centerView(graphData);
        pendingDataChange.current = false;
        initialCenteringComplete.current = true;
        
        setTimeout(() => {
          applyInitialZoom();
          
          currentOperation.current = null;
          
          console.log("Initial centering and zooming completed");
        }, 400);
        
        return newBoundingBox;
      }, 500);
    }
    return null;
  }, [isGraphReady, centerView, applyInitialZoom, graphRef]);

  useEffect(() => {
    const handleResize = () => {
      if (!graphRef.current) return;
      
      try {
        const currentWidth = window.innerWidth;
        const currentHeight = window.innerHeight;
        
        const lastRenderDimensions = { 
          current: { 
            width: graphRef.current._canvas?.parentElement?.clientWidth || window.innerWidth,
            height: graphRef.current._canvas?.parentElement?.clientHeight || window.innerHeight
          } 
        };
        
        const significantChange = 
          Math.abs(currentWidth - lastRenderDimensions.current.width) > 50 ||
          Math.abs(currentHeight - lastRenderDimensions.current.height) > 50;
        
        if (significantChange) {
          console.log("Window resized significantly, recentering view");
          pendingDataChange.current = true;
        }
      } catch (err) {
        console.error("Error in resize handler:", err);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [graphRef]);

  return {
    pendingDataChange,
    initialCenteringComplete,
    initializeGraphRender
  };
};
